import React, { useState, useEffect } from 'react';
import { fabric } from 'fabric-with-erasing';
import useZoomKeyboardShortcuts from '../../hooks/useZoomKeyboardShortcuts';
import './FloatingZoomControls.css';

const FloatingZoomControls = ({ canvas, onZoomChange }) => {
  const [zoomLevel, setZoomLevel] = useState(1);
  // Persisted visibility preference key
  const VISIBILITY_KEY = 'floatingZoomVisible';

  // Default: hidden on first load (user asked controls hidden by default)
  const [isVisible, setIsVisible] = useState(() => {
    if (typeof window === 'undefined') return false;
    const stored = window.localStorage.getItem(VISIBILITY_KEY);
    if (stored !== null) return stored === 'true';
    return false; // default hidden
  });

  // Track whether user explicitly toggled visibility (used to decide persistence)
  const [hasUserOverride, setHasUserOverride] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(VISIBILITY_KEY) !== null;
  });

  // Zoom limits
  const MIN_ZOOM = 0.2;
  const MAX_ZOOM = 5;
  const ZOOM_STEP = 0.2;

  // Update zoom level when canvas changes
  useEffect(() => {
    if (!canvas) return;

    const handleZoom = () => {
      const currentZoom = canvas.getZoom();
      setZoomLevel(currentZoom);
      if (onZoomChange) {
        onZoomChange(currentZoom);
      }
    };

    // Listen to multiple canvas events for zoom changes
    canvas.on('after:render', handleZoom);
    canvas.on('mouse:wheel', handleZoom);
    canvas.on('zoom:changed', handleZoom);
    
    // Set initial zoom level
    const initialZoom = canvas.getZoom();
    setZoomLevel(initialZoom);

    return () => {
      canvas.off('after:render', handleZoom);
      canvas.off('mouse:wheel', handleZoom);
      canvas.off('zoom:changed', handleZoom);
    };
  }, [canvas, onZoomChange]);

  const applyCenteredZoom = (newZoom) => {
    if (!canvas) {
      return;
    }
    
    // Clamp zoom to limits
    const clampedZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, newZoom));
    
    // Apply zoom but preserve the current viewport translation (i.e. don't
    // forcibly recenter). This keeps the visible area where the user panned
    // to and zooms relative to that position.
    try {
      const currentZoom = typeof canvas.getZoom === 'function' ? canvas.getZoom() : (canvas.viewportTransform ? canvas.viewportTransform[0] : 1);
      const vt = canvas.viewportTransform || [currentZoom || 1, 0, 0, currentZoom || 1, 0, 0];

      if (currentZoom && vt) {
        const factor = clampedZoom / (currentZoom || 1);
        // Scale the translation so the viewport keeps pointing at the same
        // region of the canvas after the zoom change.
        const newTx = vt[4] * factor;
        const newTy = vt[5] * factor;
        const newVT = [clampedZoom, 0, 0, clampedZoom, newTx, newTy];
        if (typeof canvas.setViewportTransform === 'function') {
          canvas.setViewportTransform(newVT);
        } else {
          canvas.viewportTransform = newVT;
        }
      } else {
        // Fallback: center if we can't determine current transform
        const vw = canvas.getWidth();
        const vh = canvas.getHeight();
        const tx = (vw - vw * clampedZoom) / 2;
        const ty = (vh - vh * clampedZoom) / 2;
        const fallbackVT = [clampedZoom, 0, 0, clampedZoom, tx, ty];
        if (typeof canvas.setViewportTransform === 'function') {
          canvas.setViewportTransform(fallbackVT);
        } else {
          canvas.viewportTransform = fallbackVT;
        }
      }

      canvas.requestRenderAll();
      setZoomLevel(clampedZoom);
    } catch (error) {
      // ignore zoom errors
    }
  };

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    // On resize do not override an explicit user preference. If the user has
    // a persisted preference (hasUserOverride), respect it. Otherwise keep the
    // default hidden state the user requested.
    const onResize = () => {
      // no-op; we intentionally avoid forcing visibility on desktop so user
      // hide persists and the toggle works as expected.
    };

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [hasUserOverride]);

  const handleZoomIn = () => {
    const newZoom = Math.min(MAX_ZOOM, zoomLevel + ZOOM_STEP);
    applyCenteredZoom(newZoom);
  };

  const handleZoomOut = () => {
    const newZoom = Math.max(MIN_ZOOM, zoomLevel - ZOOM_STEP);
    applyCenteredZoom(newZoom);
  };

  const handleZoomReset = () => {
    // Simple reset: go back to 1x zoom
    if (canvas) {
      try {
        // If there's a background image, restore it to fit the canvas (same logic as initial load)
        const bg = canvas.backgroundImage;
        if (bg && bg.width && bg.height) {
          const fitScale = Math.min(canvas.width / bg.width, canvas.height / bg.height) || 1;
          const newWidth = bg.width * fitScale;
          const left = canvas.width / 2 - newWidth / 2;
          // initial load used top = 0
          const top = 0;
          canvas.setBackgroundImage(bg, canvas.renderAll.bind(canvas), {
            scaleX: fitScale,
            scaleY: fitScale,
            left,
            top,
            originX: 'left',
            originY: 'top',
          });
        }

        // Reset viewport so image fills the viewport as on first load
        if (typeof canvas.setViewportTransform === 'function') {
          canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
        } else {
          canvas.viewportTransform = [1, 0, 0, 1, 0, 0];
        }
        canvas.requestRenderAll();
        setZoomLevel(1);
      } catch (e) {
        // fallback
        applyCenteredZoom(1);
      }
    }
  };

  const handleZoomToFit = () => {
    // Simple fit: zoom out to see whole canvas
    if (canvas) {
      // Compute a fit zoom so the canvas content fits the viewport
      try {
        const vw = canvas.getWidth();
        const vh = canvas.getHeight();
        // Assuming canvas content size equals canvas dimensions in this app
        const fitZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, Math.min(vw / vw, vh / vh) || 1));
        // fallback to 0.5 if fitZoom is 1
        const targetZoom = fitZoom === 1 ? 0.5 : fitZoom;
        applyCenteredZoom(targetZoom);
      } catch (e) {
        applyCenteredZoom(0.5);
      }
    }
  };

  // Add keyboard shortcuts
  useZoomKeyboardShortcuts(canvas, handleZoomIn, handleZoomOut, handleZoomReset, handleZoomToFit);

  if (!canvas) return null;

  const handleToggleVisibility = () => {
    setIsVisible((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(VISIBILITY_KEY, next ? 'true' : 'false');
      } catch (e) {
        // ignore storage errors
      }
      return next;
    });
    setHasUserOverride(true);
  };

  const handleReopen = () => {
    setIsVisible(true);
    try {
      window.localStorage.setItem(VISIBILITY_KEY, 'true');
    } catch (e) {}
    setHasUserOverride(true);
  };

  // When controls are hidden still render a small reopen button so the user
  // can bring the controls back without needing to re-mount the component.
  if (!isVisible) {
    return (
      <div className="floating-zoom-controls-closed" data-onboarding="zoom-controls">
        <button
          className="zoom-controls-reopen"
          onClick={handleReopen}
          title="Show Zoom Controls"
          aria-label="Show Zoom Controls"
        >
          {/* Magnifier (search) icon for clearer affordance */}
          <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
            <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398l3.85 3.85a1 1 0 0 0 1.415-1.415l-3.85-3.85zM6.5 11a4.5 4.5 0 1 1 0-9 4.5 4.5 0 0 1 0 9z" />
          </svg>
        </button>
      </div>
    );
  }

  return (
    <div className="floating-zoom-controls" data-onboarding="zoom-controls">
      <div className="zoom-controls-group">
        <button
          className="zoom-btn zoom-in"
          onClick={handleZoomIn}
          disabled={zoomLevel >= MAX_ZOOM}
          title="Zoom In (Ctrl +)"
          aria-label="Zoom In"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M8 2a.5.5 0 0 1 .5.5v5h5a.5.5 0 0 1 0 1h-5v5a.5.5 0 0 1-1 0v-5h-5a.5.5 0 0 1 0-1h5v-5A.5.5 0 0 1 8 2"/>
          </svg>
        </button>
        
        <button
          className="zoom-level-display"
          onClick={handleZoomReset}
          title="Reset to Fit (Click to center and fit canvas)"
          aria-label={`Current zoom: ${Math.round(zoomLevel * 100)}% - Click to reset`}
        >
          {Math.round(zoomLevel * 100)}%
        </button>
        
        <button
          className="zoom-btn zoom-out"
          onClick={handleZoomOut}
          disabled={zoomLevel <= MIN_ZOOM}
          title="Zoom Out (Ctrl -)"
          aria-label="Zoom Out"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M2 7.5a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 0 1h-11A.5.5 0 0 1 2 7.5"/>
          </svg>
        </button>
        
        <button
          className="zoom-btn zoom-fit"
          onClick={handleZoomToFit}
          title="Fit Canvas to Screen"
          aria-label="Fit Canvas to Screen"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M1.5 1a.5.5 0 0 0-.5.5v4a.5.5 0 0 1-1 0v-4A1.5 1.5 0 0 1 1.5 0h4a.5.5 0 0 1 0 1h-4zM10 .5a.5.5 0 0 1 .5-.5h4A1.5 1.5 0 0 1 16 1.5v4a.5.5 0 0 1-1 0v-4a.5.5 0 0 0-.5-.5h-4a.5.5 0 0 1-.5-.5zM.5 10a.5.5 0 0 1 .5.5v4a.5.5 0 0 0 .5.5h4a.5.5 0 0 1 0 1h-4A1.5 1.5 0 0 1 0 14.5v-4a.5.5 0 0 1 .5-.5zm15 0a.5.5 0 0 1 .5.5v4a1.5 1.5 0 0 1-1.5 1.5h-4a.5.5 0 0 1 0-1h4a.5.5 0 0 0 .5-.5v-4a.5.5 0 0 1 .5-.5z"/>
          </svg>
        </button>
      </div>
      
      {/* Optional: Hide/Show toggle */}
      <button
        className="zoom-controls-toggle"
        onClick={handleToggleVisibility}
        title="Hide Zoom Controls"
        aria-label="Toggle Zoom Controls"
      >
        <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
          <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
          <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z"/>
        </svg>
      </button>
    </div>
  );
};

export default FloatingZoomControls;
